import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { apiGet } from "@/lib/api";
import { SiteHeading, useReviewLink } from "@/lib/siteContent";

const SEED_TESTIMONIALS = [
  {
    review_id: "seed-1",
    author_name: "Jimmy R.",
    rating: 5,
    body: "Their attention to detail is next level. You can tell they treat each vehicle like a work of art.",
    source: "site",
  },
  {
    review_id: "seed-2",
    author_name: "Maria S.",
    rating: 5,
    body: "I was amazed at how they transformed my SUV! The interior looks brand new. Mobile service is so convenient.",
    source: "site",
  },
  {
    review_id: "seed-3",
    author_name: "David T.",
    rating: 5,
    body: "Got paint correction and ceramic coating on my BMW. Swirl marks are gone and water beading is incredible.",
    source: "site",
  },
];

const Testimonials = () => {
  const reviewLink = useReviewLink();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average: 5, total: 0 });

  useEffect(() => {
    apiGet("/reviews", { limit: 9 })
      .then((d) => setReviews(d.reviews?.length ? d.reviews : SEED_TESTIMONIALS))
      .catch(() => setReviews(SEED_TESTIMONIALS));
    apiGet("/reviews/stats").then(setStats).catch(() => {});
  }, []);

  const display = reviews.length ? reviews : SEED_TESTIMONIALS;

  return (
    <section
      id="reviews"
      data-testid="testimonials-section"
      className="py-16 sm:py-24 lg:py-32 bg-gradient-to-b from-[#050505] to-[#0A0A0A] relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-sky-500 text-xs tracking-[0.3em] uppercase mb-4 block">
            Testimonials {stats.google_count > 0 && "· Live from Google"}
          </span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
            <SiteHeading k="testimonials.title" />
          </h2>
          <div className="flex items-center justify-center gap-2 mb-2">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-sky-500 text-sky-500" />
            ))}
          </div>
          {stats.total > 0 && (
            <p className="text-gray-400 text-sm">
              <span className="text-white font-semibold">{stats.average.toFixed(1)}</span> avg ·{" "}
              {stats.total} review{stats.total === 1 ? "" : "s"}
            </p>
          )}
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {display.slice(0, 9).map((r, i) => (
            <motion.div
              key={r.review_id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="rounded-2xl p-6 bg-white/[0.02] border border-white/10 hover:border-sky-500/30 transition-colors relative"
              data-testid={`testimonial-${r.review_id}`}
            >
              <Quote className="w-7 h-7 text-sky-500/30 mb-3" />
              <div className="flex items-center gap-1 mb-3">
                {[...Array(r.rating || 5)].map((_, j) => (
                  <Star key={j} className="w-3.5 h-3.5 fill-sky-500 text-sky-500" />
                ))}
              </div>
              {r.title && <p className="text-white font-semibold mb-1">{r.title}</p>}
              <p className="text-gray-300 text-sm leading-relaxed mb-4 line-clamp-6">"{r.body}"</p>

              {r.media?.length > 0 && (
                <div className="grid grid-cols-3 gap-1.5 mb-4">
                  {r.media.slice(0, 3).map((m, k) => (
                    <div key={k} className="aspect-square rounded-md overflow-hidden bg-white/5">
                      {m.type === "video" ? (
                        <video src={m.data_url} className="w-full h-full object-cover" muted />
                      ) : (
                        <img src={m.data_url} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-3 pt-3 border-t border-white/5">
                {r.author_picture ? (
                  <img src={r.author_picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center text-sky-300 text-xs font-bold">
                    {(r.author_name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">{r.author_name || "Customer"}</p>
                  <p className="text-gray-500 text-[10px] uppercase tracking-widest">
                    {r.source === "google" ? "Google Review" : "Verified Customer"}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        {reviewLink && (
          <div className="text-center mt-10">
            <a href={reviewLink} target="_blank" rel="noreferrer" data-testid="leave-google-review" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-sky-500 hover:bg-sky-400 text-white text-sm font-medium transition-colors">
              <Star className="w-4 h-4 fill-white" /> Leave us a Google review
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
